import { memo } from 'react';
import type { Cardinality, Relationship, Entity } from '@veodiagram/shared';

// ============= Helpers =============

function cardinalityDescription(cardinality: Cardinality): string {
  switch (cardinality) {
    case '1:1': return 'One-to-One (1:1)';
    case '1:N': return 'One-to-Many (1:N)';
    case 'N:1': return 'Many-to-One (N:1)';
    case 'N:M': return 'Many-to-Many (N:M)';
    default:    return cardinality;
  }
}

function cardinalityColor(cardinality: Cardinality): string {
  switch (cardinality) {
    case '1:1': return 'text-indigo-600 bg-indigo-50 border-indigo-200';
    case '1:N': return 'text-green-600 bg-green-50 border-green-200';
    case 'N:1': return 'text-amber-600 bg-amber-50 border-amber-200';
    case 'N:M': return 'text-pink-600 bg-pink-50 border-pink-200';
    default:    return 'text-slate-600 bg-slate-50 border-slate-200';
  }
}

// ============= RelationshipPanel =============

interface RelationshipPanelProps {
  relationship: Relationship | null;
  entities: Entity[];
  onClose: () => void;
  onJumpToEntity: (entityId: string) => void;
}

export const RelationshipPanel = memo(function RelationshipPanel({
  relationship,
  entities,
  onClose,
  onJumpToEntity,
}: RelationshipPanelProps) {
  if (!relationship) return null;

  const fromEntity = entities.find(e => e.id === relationship.from);
  const toEntity = entities.find(e => e.id === relationship.to);
  const fromName = fromEntity?.name || relationship.from;
  const toName = toEntity?.name || relationship.to;

  return (
    <div className="bg-white rounded-lg shadow-xl border border-slate-200 w-72 overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-slate-800 text-white">
        <div className="flex items-center gap-2 min-w-0">
          <svg className="w-4 h-4 text-slate-300 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
          </svg>
          <span className="text-sm font-semibold truncate">
            {fromName} → {toName}
          </span>
        </div>
        <button
          onClick={onClose}
          className="ml-2 p-1 rounded hover:bg-slate-700 text-slate-300 hover:text-white transition-colors shrink-0"
          aria-label="Close"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Body */}
      <div className="p-4 space-y-3">
        {/* Cardinality */}
        <div>
          <div className="text-xs font-medium text-slate-500 mb-1">Cardinality</div>
          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${cardinalityColor(relationship.cardinality)}`}>
            {cardinalityDescription(relationship.cardinality)}
          </span>
        </div>

        {/* FK Mapping */}
        {(relationship.fromColumn || relationship.toColumn) && (
          <div>
            <div className="text-xs font-medium text-slate-500 mb-1">Foreign Key</div>
            <div className="text-sm font-mono bg-slate-50 rounded px-3 py-2 text-slate-700 border border-slate-200">
              {relationship.fromColumn && (
                <span className="text-indigo-600">{fromName}.{relationship.fromColumn}</span>
              )}
              <span className="text-slate-400 mx-1">→</span>
              {relationship.toColumn && (
                <span className="text-indigo-600">{toName}.{relationship.toColumn}</span>
              )}
            </div>
          </div>
        )}

        {/* Referential Actions */}
        {(relationship.onDelete || relationship.onUpdate) && (
          <div>
            <div className="text-xs font-medium text-slate-500 mb-1">Referential Actions</div>
            <div className="space-y-1">
              {relationship.onDelete && (
                <div className="flex justify-between text-xs">
                  <span className="text-slate-500">On Delete:</span>
                  <span className="font-medium text-slate-700">{relationship.onDelete}</span>
                </div>
              )}
              {relationship.onUpdate && (
                <div className="flex justify-between text-xs">
                  <span className="text-slate-500">On Update:</span>
                  <span className="font-medium text-slate-700">{relationship.onUpdate}</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Required */}
        {relationship.isRequired && (
          <div className="flex items-center gap-1.5 text-xs text-amber-600">
            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            Required relationship
          </div>
        )}

        {/* Jump buttons */}
        <div className="flex gap-2 pt-1 border-t border-slate-100">
          {fromEntity && (
            <button
              onClick={() => onJumpToEntity(fromEntity.id)}
              className="flex-1 px-2 py-1.5 text-xs bg-slate-100 text-slate-600 rounded hover:bg-slate-200 transition-colors font-medium truncate"
            >
              Go to {fromName}
            </button>
          )}
          {toEntity && (
            <button
              onClick={() => onJumpToEntity(toEntity.id)}
              className="flex-1 px-2 py-1.5 text-xs bg-slate-100 text-slate-600 rounded hover:bg-slate-200 transition-colors font-medium truncate"
            >
              Go to {toName}
            </button>
          )}
        </div>
      </div>
    </div>
  );
});

RelationshipPanel.displayName = 'RelationshipPanel';
